import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Sparkle, Wind, Star, ArrowRight } from '@phosphor-icons/react';

const ITEMS_BY_ID = (id, list) => list.find((i) => i.id === id);

const ResonanceHub = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () => api.get('/resonance/me').then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const redeem = async (item_id) => {
    setBusy(item_id);
    try {
      const { data: r } = await api.post('/resonance/redeem', { item_id });
      toast.success(`Redeemed ${r.item.label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'failed');
    } finally { setBusy(''); }
  };

  if (!data) return <div className="text-center py-20 text-white/65">loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-8">
        <Wind size={36} weight="duotone" className="text-bx-cyan mx-auto" />
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan mt-2">creator resonance</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
          Echoes from your <span className="shimmer-text">bubbles</span>
        </h1>
        <p className="text-white/65 mt-3 max-w-lg mx-auto text-sm">
          You don't earn money for what you create. You earn resonance — and resonance buys you magic.
        </p>
      </div>

      <div className="grad-border p-6 mb-8 text-center">
        <div className="font-display text-6xl font-black shimmer-text">{data.balance}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
          balance · {data.lifetime} earned all time
        </div>
        <Link to={`/creator/${user?.username}`} data-testid="resonance-to-profile"
          className="inline-flex items-center gap-1 text-xs text-bx-cyan font-bold mt-3 hover:underline">
          see my constellation <ArrowRight size={12} weight="bold" />
        </Link>
      </div>

      <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
        <Star size={18} weight="fill" className="text-bx-gold" /> Redeem
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.catalog.map((item) => (
          <motion.div key={item.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="glass-hi rounded-2xl p-5" data-testid={`catalog-${item.id}`}>
            <div className="flex items-baseline gap-2">
              <Sparkle size={14} weight="fill" className="text-bx-magenta" />
              <div className="font-display font-bold flex-1">{item.label}</div>
              <div className="font-display font-black text-bx-cyan">{item.cost}</div>
            </div>
            <p className="text-xs text-white/55 mt-1">{item.desc}</p>
            <button disabled={busy === item.id || data.balance < item.cost}
              onClick={() => redeem(item.id)}
              data-testid={`redeem-${item.id}`}
              className="mt-3 w-full btn-primary py-2.5 rounded-full text-xs font-bold disabled:opacity-30">
              {data.balance < item.cost ? `need ${item.cost - data.balance} more` : 'redeem'}
            </button>
          </motion.div>
        ))}
      </div>

      {data.recent.length > 0 && (
        <>
          <h2 className="font-display text-xl font-bold mb-3 mt-10">Recent echoes</h2>
          <div className="space-y-1.5">
            {data.recent.map((r, i) => (
              <div key={i} className="text-xs text-white/55 flex items-center gap-2 glass rounded-xl px-3 py-2">
                <Sparkle size={12} weight="fill" className="text-bx-cyan" />
                <span className="flex-1 capitalize">{r.kind.replace(/_/g, ' ')}</span>
                <span className="text-bx-volt font-bold">+{r.amount}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ResonanceHub;
